<template>
  <div class="home-sec">
    <APageHead
      title="홈 진열 관리"
      sub="앱 홈 화면에 노출할 추천·기획 도서 섹션을 만들고 순서를 편성합니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="menu_book" @click="goTextbooks">교재 목록</ABtn>
        <ABtn
          kind="primary"
          icon="add"
          :disabled="apiMissing"
          :title="apiMissing ? '홈 진열 API 배포 후 사용할 수 있습니다' : ''"
          @click="openCreate"
        >섹션 등록</ABtn>
      </template>
    </APageHead>

    <!-- 섹션을 만들어도 도서를 안 담거나 노출을 안 켜면 앱에는 아무것도 안 보입니다. -->
    <div v-if="!loading && pendingCount > 0" class="home-sec__banner">
      <i class="material-icons-round">report_problem</i>
      <div>
        <b>앱에 제대로 노출되지 않는 섹션이 {{ pendingCount }}건 있습니다.</b>
        <span>
          <template v-if="emptyCount">도서 미진열 {{ emptyCount }}건</template>
          <template v-if="emptyCount && inactiveCount"> · </template>
          <template v-if="inactiveCount">미노출 {{ inactiveCount }}건</template>
          <template v-if="unpublishedSectionCount">
            · 미공개 교재 포함 {{ unpublishedSectionCount }}건
          </template>
          — <b>도서 진열 → 노출 켜기</b> 순서로 진행하세요.
        </span>
      </div>
    </div>

    <!-- KPI. API 가 없을 땐 전부 0 이라 오히려 헷갈리므로 감춥니다. -->
    <div v-if="!apiMissing" class="home-sec__stats">
      <ACard><AStat label="전체 섹션" :value="sections.length" unit="건" /></ACard>
      <ACard><AStat label="앱 노출 중" :value="activeCount" unit="건" /></ACard>
      <ACard><AStat label="진열 도서" :value="totalItemCount" unit="권" /></ACard>
      <ACard><AStat label="점검 필요" :value="pendingCount" unit="건" /></ACard>
    </div>

    <ACard>
      <template #title>
        <span class="home-sec__sec-title">
          <i class="material-icons-round home-sec__sec-icon">dashboard_customize</i>
          홈 섹션 목록
        </span>
      </template>
      <template #sub>위에 있는 섹션이 앱 홈에서도 위에 노출됩니다.</template>
      <template #action>
        <!-- 순서를 건드리기 전에는 저장 버튼 자체를 렌더하지 않습니다. -->
        <template v-if="orderDirty">
          <APill kind="warn" dot>순서 변경됨</APill>
          <ABtn size="sm" kind="ghost" icon="undo" @click="resetOrder">되돌리기</ABtn>
          <ABtn size="sm" kind="primary" icon="save" :loading="savingOrder" @click="saveOrder">
            순서 저장
          </ABtn>
        </template>
        <APill v-else kind="info">{{ sections.length }}건</APill>
      </template>

      <div v-if="loading" class="home-sec__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>홈 섹션을 불러오는 중...</p>
      </div>

      <!-- 백엔드 배포 전. 오류 모달로 놀래키는 대신 무엇을 기다리는 중인지 알려줍니다. -->
      <AEmpty
        v-else-if="apiMissing"
        title="홈 진열 API가 아직 준비되지 않았습니다"
        sub="백엔드에 요청한 /emp/home/sections 가 배포되면 이 화면은 바로 동작합니다."
      >
        <template #action>
          <ABtn kind="ghost" icon="refresh" @click="fetchSections">다시 확인</ABtn>
        </template>
      </AEmpty>

      <AEmpty
        v-else-if="!sections.length"
        title="등록된 홈 섹션이 없습니다"
        sub="'섹션 등록'으로 Kong &amp; Park Picks, TOPIK 추천 도서 같은 진열대를 먼저 만드세요."
      >
        <template #action>
          <ABtn kind="primary" icon="add" @click="openCreate">첫 섹션 만들기</ABtn>
        </template>
      </AEmpty>

      <ATable v-else :columns="columns" :rows="sections" row-key="idx" striped bordered>
        <template #cell:_order="{ index }">
          <div class="home-sec__order">
            <span class="home-sec__order-no">{{ index + 1 }}</span>
            <div class="home-sec__order-btns">
              <ABtn
                size="sm"
                kind="ghost"
                icon="arrow_upward"
                icon-only
                title="위로"
                :disabled="index === 0"
                @click="move(index, -1)"
              />
              <ABtn
                size="sm"
                kind="ghost"
                icon="arrow_downward"
                icon-only
                title="아래로"
                :disabled="index === sections.length - 1"
                @click="move(index, 1)"
              />
            </div>
          </div>
        </template>

        <template #cell:title="{ row }">
          <button type="button" class="home-sec__name" @click="goDetail(row)">
            <span class="home-sec__name-ko">{{ pickTitle(row) }}</span>
            <span class="home-sec__name-key">{{ row.section_key }}</span>
          </button>
        </template>

        <template #cell:display_type="{ row }">
          <span class="home-sec__type">
            <i class="material-icons-round">{{ displayTypeIcon(row.display_type) }}</i>
            {{ displayTypeLabel(row.display_type) }}
          </span>
        </template>

        <template #cell:display_limit="{ row }">
          <span class="home-sec__mono">{{ row.display_limit ? `${row.display_limit}권` : '전체' }}</span>
        </template>

        <template #cell:item_count="{ row }">
          <div class="home-sec__items">
            <APill v-if="!row.item_count" kind="err" dot>도서 없음</APill>
            <template v-else>
              <span class="home-sec__mono">{{ row.item_count }}권</span>
              <APill v-if="row.unpublished_count" kind="warn" dot>
                미공개 {{ row.unpublished_count }}
              </APill>
            </template>
          </div>
        </template>

        <template #cell:active_yn="{ row }">
          <div class="home-sec__active">
            <button
              type="button"
              :class="['p-switch', { 'is-on': row.active_yn === 'Y' }]"
              :title="row.active_yn === 'Y' ? '앱 노출 중 — 끄기' : '미노출 — 켜기'"
              @click="toggleActive(row)"
            ></button>
            <APill :kind="row.active_yn === 'Y' ? 'ok' : 'err'" dot>
              {{ row.active_yn === 'Y' ? '노출 중' : '미노출' }}
            </APill>
          </div>
        </template>

        <template #cell:_actions="{ row }">
          <div class="home-sec__actions">
            <ABtn size="sm" kind="primary" icon="library_books" @click="goDetail(row)">도서 진열</ABtn>
            <ABtn size="sm" kind="ghost" icon="edit" icon-only title="섹션 정보 수정" @click="openEdit(row)" />
            <ABtn size="sm" kind="danger" icon="delete" icon-only title="섹션 삭제" @click="removeSection(row)" />
          </div>
        </template>
      </ATable>
    </ACard>

    <HomeSectionFormModal
      v-model="showForm"
      :section="editingSection"
      :sections="serverSections"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import HomeSectionFormModal from "./components/HomeSectionFormModal.vue";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";
import {
  pickTitle,
  displayTypeLabel,
  displayTypeIcon,
  normalizeSection,
  moveItem,
  orderPayload,
  sameOrder,
} from "@/utils/homeSection";

export default {
  name: "SystemHomeSections",
  components: { APageHead, ACard, AStat, ABtn, APill, ATable, AEmpty, HomeSectionFormModal },
  // 순서를 바꿔 두고 상세로 넘어가면 조용히 날아갑니다. 목록↔상세를 오가는 화면이라 필수입니다.
  async beforeRouteLeave() {
    if (!this.orderDirty) return true;
    const ok = await swal.fire({
      icon: "warning",
      title: "저장하지 않은 순서가 있습니다",
      text: "이 화면을 벗어나면 바꾼 순서가 사라집니다.",
      showCancelButton: true,
      confirmButtonText: "저장하지 않고 나가기",
      cancelButtonText: "여기 남기",
    });
    return ok.isConfirmed;
  },
  data() {
    return {
      loading: true,
      savingOrder: false,
      // 백엔드에 /emp/home/sections 자체가 없는 상태(404). 오류가 아니라 "아직 안 나옴"으로 다룹니다.
      apiMissing: false,
      // sections = 화면이 만지는 작업본, serverSections = 마지막으로 서버와 맞춘 스냅샷
      sections: [],
      serverSections: [],
      showForm: false,
      editingSection: null,
      columns: [
        { key: "_order", label: "순서", width: 110, align: "center" },
        { key: "title", label: "섹션", width: 260 },
        { key: "display_type", label: "노출 방식", width: 140, align: "center" },
        { key: "display_limit", label: "노출 개수", width: 100, align: "center" },
        { key: "item_count", label: "진열 도서", width: 150, align: "center" },
        { key: "active_yn", label: "앱 노출", width: 150, align: "center" },
        { key: "_actions", label: "관리", width: 200, align: "right" },
      ],
    };
  },
  computed: {
    activeCount() {
      return this.sections.filter((s) => s.active_yn === "Y").length;
    },
    inactiveCount() {
      return this.sections.filter((s) => s.active_yn !== "Y").length;
    },
    emptyCount() {
      return this.sections.filter((s) => !s.item_count).length;
    },
    unpublishedSectionCount() {
      return this.sections.filter((s) => s.unpublished_count > 0).length;
    },
    totalItemCount() {
      return this.sections.reduce((sum, s) => sum + (s.item_count || 0), 0);
    },
    // 도서가 없거나, 노출이 꺼져 있거나, 미공개 교재가 섞인 섹션
    pendingCount() {
      return this.sections.filter(
        (s) => !s.item_count || s.active_yn !== "Y" || s.unpublished_count > 0
      ).length;
    },
    orderDirty() {
      return !sameOrder(this.sections, this.serverSections, "idx");
    },
  },
  created() {
    this.fetchSections();
  },
  methods: {
    pickTitle,
    displayTypeLabel,
    displayTypeIcon,
    goTextbooks() {
      this.$router.push({ name: "ContentsTextbooks" });
    },
    goDetail(row) {
      this.$router.push({
        name: "SystemHomeSectionDetail",
        params: { sectionIdx: row.idx },
      });
    },
    /**
     * 순서를 바꿔 둔 채로 다른 작업을 하면, 그 작업의 재조회에 묻혀 순서가 조용히 사라집니다.
     * 재조회를 부르는 모든 동작 앞에 세워 둡니다.
     * @returns {Promise<boolean>} 계속 진행해도 되면 true
     */
    async ensureOrderSaved() {
      if (!this.orderDirty) return true;
      const ok = await swal.fire({
        icon: "warning",
        title: "저장하지 않은 순서가 있습니다",
        text: "먼저 순서를 저장한 뒤 이어서 진행합니다. 순서를 버리려면 '되돌리기'를 누르세요.",
        showCancelButton: true,
        confirmButtonText: "순서 저장 후 계속",
        cancelButtonText: "취소",
      });
      if (!ok.isConfirmed) return false;
      return this.saveOrder();
    },
    async openCreate() {
      if (!(await this.ensureOrderSaved())) return;
      this.editingSection = null;
      this.showForm = true;
    },
    async openEdit(row) {
      if (!(await this.ensureOrderSaved())) return;
      this.editingSection = row;
      this.showForm = true;
    },
    onSaved() {
      this.fetchSections();
    },
    async fetchSections() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/emp/home/sections",
          loadingBar: true,
        });
        const items = (res.data?.items || res.data || []).map(normalizeSection);
        // 서버가 sort_order 로 정렬해 주지만, 값이 비어 있어도 화면 순서가 흔들리지 않게 한 번 더 정렬합니다.
        items.sort((a, b) => a.sort_order - b.sort_order || a.idx - b.idx);
        this.sections = items;
        this.serverSections = [...items];
        this.apiMissing = false;
      } catch (e) {
        this.sections = [];
        this.serverSections = [];

        // 404 = 아직 배포되지 않은 API. 화면에 들어올 때마다 오류 모달을 띄울 일이 아니라,
        // 무엇을 기다리는 중인지 알려주는 빈 상태로 떨어뜨립니다.
        // (배포 후에는 목록이 비어도 200 + 빈 배열이 오므로 이 분기를 타지 않습니다)
        if (apiErrorStatus(e) === 404) {
          this.apiMissing = true;
          console.info("홈 진열 API 미배포 (/emp/home/sections 404)");
        } else {
          this.apiMissing = false;
          console.error("홈 섹션 목록 조회 실패:", e);
          swal.fire("오류", apiErrorMessage(e, "홈 섹션을 불러오지 못했습니다."), "error");
        }
      } finally {
        this.loading = false;
      }
    },
    /**
     * 위/아래 이동은 로컬 배열만 바꿉니다.
     * 클릭마다 저장하면 연타 시 요청이 쌓이고, 중간에 하나 실패하면 화면과 서버가 어긋납니다.
     */
    move(index, dir) {
      this.sections = moveItem(this.sections, index, dir);
    },
    resetOrder() {
      this.sections = [...this.serverSections];
    },
    /** @returns {Promise<boolean>} 저장에 성공했으면 true */
    async saveOrder() {
      this.savingOrder = true;
      try {
        const res = await this.$axios({
          method: "put",
          url: "/emp/home/sections/order",
          data: { orders: orderPayload(this.sections, "idx") },
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: "섹션 순서를 저장했습니다." });
        // 응답으로 최종 목록을 주면 재조회를 아낍니다.
        const items = res.data?.items;
        if (Array.isArray(items) && items.length) {
          const next = items.map(normalizeSection);
          next.sort((a, b) => a.sort_order - b.sort_order || a.idx - b.idx);
          this.sections = next;
          this.serverSections = [...next];
        } else {
          await this.fetchSections();
        }
        return true;
      } catch (e) {
        console.error("섹션 순서 저장 실패:", e);
        swal.fire("저장 실패", apiErrorMessage(e, "순서를 저장하지 못했습니다."), "error");
        // 실패했는데 화면만 바뀐 채로 두면 다음 저장이 엉킵니다. 서버 상태로 되돌립니다.
        await this.fetchSections();
        return false;
      } finally {
        this.savingOrder = false;
      }
    },
    /**
     * 노출 토글 = 앱 홈 노출 on/off.
     * 도서가 한 권도 없는 섹션을 켜려 하면 서버가 409로 막습니다.
     */
    async toggleActive(row) {
      if (!(await this.ensureOrderSaved())) return;
      const next = row.active_yn === "Y" ? "N" : "Y";
      const name = this.pickTitle(row);

      const ok = await swal.fire({
        icon: "warning",
        title: next === "Y" ? "앱 홈 노출을 시작합니다" : "앱 홈 노출을 중단합니다",
        text:
          next === "Y"
            ? `'${name}' 섹션이 앱 홈 화면에 바로 노출됩니다.`
            : `'${name}' 섹션이 앱 홈 화면에서 사라집니다. 진열한 도서는 그대로 남습니다.`,
        showCancelButton: true,
        confirmButtonText: next === "Y" ? "노출 시작" : "노출 중단",
        cancelButtonText: "취소",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "patch",
          url: `/emp/home/sections/${row.idx}/active`,
          data: { active_yn: next },
          loadingBar: true,
        });
        this.$notifyVue({
          type: "success",
          title:
            next === "Y" ? `'${name}' 을 앱 홈에 노출합니다.` : `'${name}' 노출을 중단했습니다.`,
        });
        this.fetchSections();
      } catch (e) {
        console.error("섹션 노출 토글 실패:", e);
        const message = apiErrorMessage(e, "노출 상태를 바꾸지 못했습니다.");

        // 409 = 진열된 도서가 없는 섹션. 다음에 할 일(도서 진열)로 바로 보내 줍니다.
        if (apiErrorStatus(e) === 409) {
          const go = await swal.fire({
            icon: "warning",
            title: "노출할 수 없습니다",
            text: message,
            showCancelButton: true,
            confirmButtonText: "도서 진열하러 가기",
            cancelButtonText: "닫기",
          });
          if (go.isConfirmed) {
            this.goDetail(row);
            return;
          }
        } else {
          await swal.fire("변경 실패", message, "error");
        }
        this.fetchSections();
      }
    },
    async removeSection(row) {
      if (!(await this.ensureOrderSaved())) return;
      const name = this.pickTitle(row);
      const ok = await swal.fire({
        icon: "warning",
        title: "섹션을 삭제합니다",
        text: `'${name}' 섹션과 진열된 도서 ${row.item_count || 0}권이 함께 삭제됩니다. 되돌릴 수 없습니다.`,
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "delete",
          url: `/emp/home/sections/${row.idx}`,
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: `'${name}' 섹션을 삭제했습니다.` });
        this.fetchSections();
      } catch (e) {
        console.error("섹션 삭제 실패:", e);
        const message = apiErrorMessage(e, "섹션을 삭제하지 못했습니다.");
        // 409 = 아직 노출 중인 섹션. 앱 화면이 갑자기 비는 걸 서버가 막습니다.
        if (apiErrorStatus(e) === 409) {
          swal.fire("삭제할 수 없습니다", message, "warning");
        } else {
          swal.fire("삭제 실패", message, "error");
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.home-sec {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 16px;
    background: $p-warn-soft;
    border: 1px solid rgba($p-warn, 0.35);
    border-radius: $p-radius-md;

    > i {
      font-size: 20px !important;
      color: $p-warn-ink;
    }

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;

      b { font-size: 12px; font-weight: 800; color: $p-ink; }
      span { font-size: 11px; line-height: 1.5; color: $p-ink-mid; }
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__order {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__order-no {
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-soft;
    min-width: 14px;
  }

  &__order-btns {
    display: inline-flex;
    gap: 2px;
  }

  &__name {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font-family: $p-font-body;

    &:hover .home-sec__name-ko { color: $p-point-deep; }
  }

  &__name-ko {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    transition: color $p-dur $p-ease;
  }

  &__name-key {
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__type {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: $p-ink-mid;

    > i {
      font-size: 15px !important;
      color: $p-point-deep;
    }
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
  }

  &__items,
  &__active {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__actions {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
}
</style>
